/**
 * Route Preloading System
 * 
 * Preloads lazy-loaded route components before navigation to eliminate
 * chunk loading delays. Uses two strategies:
 * 1. Idle-time preload: Load all main routes during browser idle time
 * 2. Hover preload: Load specific route when user hovers over nav link
 * 
 * Technical notes:
 * - Preloading is idempotent (import() returns cached promise)
 * - No Suspense flash when navigating to preloaded routes
 * - Graceful degradation if preload fails
 */

// Registry of lazy-loaded route components for preloading
// Keys match URL paths without leading slash
const routeLoaders = {
  orders: () => import("../pages/Index"),
  customers: () => import("../pages/Customers"),
  dashboard: () => import("../pages/Dashboard"),
  products: () => import("../pages/Products"),
  settings: () => import("../pages/Settings"),
  delivery: () => import("../pages/Delivery"),
  archive: () => import("../pages/Archive"),
} as const;

export type PreloadableRoute = keyof typeof routeLoaders;

/**
 * Preload a specific route component
 * Safe to call multiple times - import() is cached
 */
export function preloadRoute(route: PreloadableRoute): void {
  routeLoaders[route]?.();
}

/**
 * Map URL path to preloadable route key
 */
export function getPreloadKey(url: string): PreloadableRoute | null {
  const path = url.replace(/^\//, ''); // Remove leading slash
  if (path in routeLoaders) {
    return path as PreloadableRoute;
  }
  return null;
}

/**
 * Preload all main routes during idle time
 * Call this after initial page load to warm the cache
 */
export function preloadMainRoutes(): void {
  Object.values(routeLoaders).forEach(loader => loader());
}
